import moment from "moment";
import querystring from "querystring";
import {
    Availabilities as AvailabilitiesSchema,
    Inventory as InventorySchema,
    ReplenishmentGroups as ReplenishmentGroupsSchema,
    Items as ItemsSchema,
} from "schemas/items";
import {
    Availability as AvailabilityRecord,
    DeliveryWindow as DeliveryWindowRecord,
    Inventory as InventoryRecord,
    ReplenishmentGroup as ReplenishmentGroupRecord,
} from "records/item";

import { normalize } from "normalizr";
import { Warehouse as WarehouseRecord } from "records/warehouse";
import { Warehouses as WarehousesSchema } from "schemas/warehouses";
import { normalizeData } from "lib/api";
import me from "models/me";
import { getStore } from "store";

interface IQuery {
    __archived: boolean;
    __company?: string;
    __fields: string;
    __limit: number;
    __meta: boolean;
    __populate: string;
    __sort: string;
    __sort_order: string;
    __view_disabled: boolean;
    [filters: string]: any;
}

interface IOptions {
    forRef?: string;
    fromRef?: string;
    company?: string;
    effectiveDateMs?: string;
}

export const getQuery = ({ filters, pageSize = 50, sortBy = "__created_on", sortOrder = "desc", viewAs }) => {
    if (!filters) {
        return null;
    }

    let query: IQuery = {
        __active_pricesheet: me._activePriceTemplate,
        __archived: false,
        __fields: "__all",
        __limit: pageSize,
        __meta: true,
        __populate: "__variant",
        __sort: sortBy,
        __sort_order: sortOrder,
        __view_disabled: false,
    };

    if (viewAs) {
        query.__company = viewAs._id;
    }

    if (filters.size > 0) {
        filters = filters.toJS();

        if (filters.__active) {
            switch (filters.__active) {
                case "active":
                    filters.__active = true;
                    break;
                case "inactive":
                    filters.__view_disabled = true;
                    filters.__active = false;
                    break;
                default:
                    filters.__view_disabled = true;
                    delete query.__active;
            }
        }

        query = { ...query, ...filters };
    }

    return {
        force: true,
        schema: ItemsSchema,
        url: `/api/v3.0/items?${querystring.stringify(query)}`,
    };
};

export const getNextQuery = (nextPage: string) => {
    return {
        force: true,
        schema: ItemsSchema,
        url: nextPage,
    };
};

const mergeEntities = (prevEntities, nextEntities) => ({
    ...prevEntities,
    ...nextEntities,
});

export const getItemAvailability = (itemId: string, { company, forRef, fromRef, effectiveDateMs }: IOptions) => ({
    force: true,
    transform: (response) => {
        if (!response) {
            return { availabilityResults: [], warehouseResults: [] };
        }

        const warehouses = normalize(response.warehouses, WarehousesSchema);

        const availabilities = normalize(response.availability, AvailabilitiesSchema);

        const warehouseCollection = warehouses.entities.warehouses;
        const warehouseIds = Object.keys(warehouseCollection || {});
        warehouseIds.forEach((id) => {
            warehouseCollection[id] = new WarehouseRecord(warehouseCollection[id]);
        });

        const availabilityCollection = availabilities.entities.itemAvailabilities;
        const availabilityIds = Object.keys(availabilityCollection || {});
        availabilityIds.forEach((id) => {
            availabilityCollection[id] = new AvailabilityRecord(availabilityCollection[id]);
        });

        const deliveryWindowCollection = availabilities.entities.itemDeliveryWindows;
        Object.keys(deliveryWindowCollection || {}).forEach((id) => {
            deliveryWindowCollection[id] = new DeliveryWindowRecord(deliveryWindowCollection[id]);
        });

        return {
            ...warehouses.entities,
            ...availabilities.entities,
            availabilityResults: availabilities.result,
            warehouseResults: warehouses.result,
        };
    },
    update: {
        itemAvailabilities: mergeEntities,
        itemDeliveryWindows: mergeEntities,
        warehouses: mergeEntities,
    },
    url: `/api/v3.0/item/${itemId}/warehouses-availability?__populate_future_inventory=true${[
        company ? `&__company=${company}` : "",
        forRef ? `&forRef=${forRef}` : "",
        fromRef ? `&fromRef=${fromRef}` : "",
        effectiveDateMs ? `&effectiveDateMs=${effectiveDateMs}` : "",
    ].join("")}`,
});

export const getItemWarehouseReplenishments = (itemId: string, warehouseId: string, forCompany?: string) => ({
    force: true,
    transform: (response) => {
        if (!response) {
            return {};
        }

        const data = normalize(response, ReplenishmentGroupsSchema);

        const collection = data.entities.replenishmentGroups;
        const replenishmentIds = Object.keys(collection || {});
        replenishmentIds.forEach((id) => {
            collection[id] = new ReplenishmentGroupRecord(collection[id]);
        });

        return {
            ...data.entities,
            results: data.result,
        };
    },
    update: {
        replenishmentGroups: mergeEntities,
    },
    url: `/api/v3.0/item/${itemId}/warehouse/${warehouseId}/replenishments${
        forCompany ? `?__company=${forCompany}` : ""
    }`,
});

export const getInventoryForWarehouse = (
    itemId: string,
    warehouseId: string,
    date: moment.Moment,
    { company, forRef, fromRef, effectiveDateMs }: IOptions
) => {
    date = date || moment.utc();
    return {
        force: true,
        transform: (response) => {
            if (!response) {
                return {};
            }

            const data = normalize(response, InventorySchema);

            const collection = data.entities.inventory;
            Object.keys(collection || {}).forEach((id) => {
                collection[id] = new InventoryRecord(collection[id]);
            });

            return {
                ...data.entities,
                results: data.result,
            };
        },
        update: {
            inventory: mergeEntities,
        },
        url: `/api/v3.0/item/${itemId}/warehouse/${warehouseId}/available-inventory?__date=${date.valueOf()}${[
            company ? `&__company=${company}` : "",
            forRef ? `&forRef=${forRef}` : "",
            fromRef ? `&fromRef=${fromRef}` : "",
            effectiveDateMs ? `&effectiveDateMs=${effectiveDateMs}` : "",
        ].join("")}`,
    };
};

export const filterProductColorVariants = (items, colorRestriction) => {
    if (colorRestriction) {
        return items.filter((item) => item[colorRestriction.key] === colorRestriction.value);
    }
    return items;
};

export const getProductColorRestriction = (product) => {
    const { scope } = getStore().getState();
    const preference = scope.brand.getPreference("product_colors_restrictions");
    return { key: preference, value: product[preference] };
};

export const getItemVariants = (
    item: any,
    options: {
        styleNumber: string;
        companyId?: string;
        currencyCode?: string;
        effectiveDate?: string;
        availableFrom?: string;
        availableUntil?: string;
    }
) => {
    const { scope } = getStore().getState();

    const query = {
        __effective_date_gte: options.effectiveDate,
        __availability_start_date_gte: options.availableFrom,
        __availability_end_date_lte: options.availableUntil,
        __active_pricesheet: me._activePriceTemplate,
        __fields: "__all",
        __limit: 1000,
        __populate: ["__currency_breakdown", "__promotions", "__variant"],
        style_number: options.styleNumber,
        __company: undefined,
        __advanced_pricing_currency_code: undefined,
    };

    if (options.companyId) {
        query.__company = options.companyId;
    }

    if (scope.brand?.isPricingPromotionEnabled(scope) && options.currencyCode) {
        query.__advanced_pricing_currency_code = options.currencyCode;
    }

    return {
        force: true,
        schema: ItemsSchema,
        transform: (response) => {
            if (!response) {
                return { _result: [] };
            }

            response = filterProductColorVariants(response.data || response, getProductColorRestriction(item));
            response.sort((a, b) => {
                if (a.color < b.color) {
                    return -1;
                }
                if (a.color > b.color) {
                    return 1;
                }
                return 0;
            });

            const data = normalizeData(response, ItemsSchema);

            return { ...data.entities, _result: data.result };
        },
        url: `/api/v3.0/items?${querystring.stringify(query)}`,
    };
};
